import React from 'react';
import { S } from '@mobily/ts-belt';
import { recordMark } from '@stitch-fix/client-analytics-reporter';
import { useViewEvent } from '@stitch-fix/event-reporter';
import { Box, Button, Grid, ModuleHeader, Placeholder } from '@stitch-fix/mode-react';
import type { BusinessLine } from '../../../../types/definitions/vendor/graphql-schema.d';
import { type GetUngatedPaginatedShopCategoryQuery, type ShopProductItemFragment, useGetUngatedPaginatedShopCategoryQuery } from './getUngatedPaginatedShopCategory';
import { type ShopCategoryGridPaginationProps, useShopCategoryGridPagination } from './useShopCategoryGridPagination';
import { useBuildShopProductItems } from './useBuildShopProductItems';
import { ModuleWrapper, type ModuleWrapperProps } from '../../../../components/ModuleWrapper';
import type { ModularComponentProps } from '../../../../components/ModularPageWrapper/ModularPageWrapper';
import type { ShopCategoryPromoCard } from '../PromoCard/PromoCard';
export type { ShopCategoryPromoCard, Theme } from '../PromoCard/PromoCard';
export const SHOP_CATEGORY_PER_PAGE = 60;
export type ShopCategoryProps = {
  categoryId: string;
  businessLine: BusinessLine;
  limit?: number;
  promoCards?: ShopCategoryPromoCard[];
} & Omit<ModuleWrapperProps, 'children' | 'alignment'>;
export { ShopCategoryGridCursorProvider, type ShopCategoryGridPaginationProps } from './useShopCategoryGridPagination';
const ShopCategoryGrid = ({
  gridItems
}: {
  gridItems: React.ReactElement[];
}) => <Box px={1} py={{
  sm: 1.5,
  md: 2
}}>
    <Grid columns={{
    sm: 2,
    md: 3,
    lg: 4
  }} spacing={{
    sm: [2, 1],
    md: [2, 1.5]
  }}>
      {gridItems}
    </Grid>
  </Box>;
const LoadingShopCategory = ({
  limit
}: {
  limit: number;
}) => {
  // match placeholder count to the product count being requested so the height doesn't jump when loaded
  const placeholders = Array.from({
    length: limit
  }, (_, index) => <Box data-testid="loading-placeholder" key={`placeholder-${index}`}>
      <Placeholder variant="rect" width="100%" height={0} pt="125%" />
      <Placeholder width={4} mt={1} />
      <Placeholder width={8} mt={0.25} />
      <Placeholder width={2} mt={0.25} />
    </Box>);
  return <ShopCategoryGrid gridItems={placeholders} />;
};
const EmptyShopCategory = ({
  categoryId
}: {
  categoryId: string;
}) => {
  const {
    ref,
    EventProvider
  } = useViewEvent({
    schema: 'category_view',
    name: 'empty_category_recommendations',
    category_id: categoryId
  });
  return <EventProvider>
      <Box px={1} py={3} style={{
      textAlign: 'center'
    }} ref={ref}>
        <ModuleHeader header="These popular picks are unavailable" subheader="We'll try to restock with fresh selects soon, check back again later." />
      </Box>
    </EventProvider>;
};
const ShopProductCards = ({
  categoryId,
  pageSize,
  shopProducts,
  promoCards
}: {
  categoryId: string;
  pageSize: number;
  shopProducts: ShopProductItemFragment[];
  promoCards?: ShopCategoryPromoCard[];
}) => {
  const {
    EventProvider,
    ref
  } = useViewEvent({
    category_id: categoryId,
    name: 'grid',
    schema: 'category_view'
  });
  const {
    shopCategory
  } = useBuildShopProductItems({
    shopProducts,
    promoCards,
    pageSize
  });
  return <EventProvider>
      <Box ref={ref}>
        <ShopCategoryGrid gridItems={shopCategory} />
      </Box>
    </EventProvider>;
};
const getShopProductConnection = (data?: GetUngatedPaginatedShopCategoryQuery) => data?.shopContent?.categoryPage?.category?.shopProducts;

// fetchMore replaces the connection by default
// append nodes and keep the latest pageInfo
const mergeShopProductPages = (previousData: GetUngatedPaginatedShopCategoryQuery, nextData: GetUngatedPaginatedShopCategoryQuery) => {
  const previousConnection = getShopProductConnection(previousData);
  const nextConnection = getShopProductConnection(nextData);
  const shopContent = previousData.shopContent;
  const categoryPage = shopContent?.categoryPage;
  const category = categoryPage?.category;
  if (!shopContent || !categoryPage || !category || !previousConnection || !nextConnection) {
    // keep showing previous data when either response is still incomplete.
    return previousData;
  }
  return {
    ...previousData,
    shopContent: {
      ...shopContent,
      categoryPage: {
        ...categoryPage,
        category: {
          ...category,
          shopProducts: {
            ...nextConnection,
            nodes: [...previousConnection.nodes, ...nextConnection.nodes]
          }
        }
      }
    }
  };
};
export const ShopCategory = (props: ShopCategoryProps & ModularComponentProps) => {
  const {
    categoryId,
    businessLine,
    limit = SHOP_CATEGORY_PER_PAGE,
    verticalSpacing,
    moduleTitle,
    moduleFooter,
    promoCards
  } = props;
  const {
    gridId,
    previousGridId,
    showMoreEnabled = false
  } = props as ShopCategoryProps & ShopCategoryGridPaginationProps;
  const {
    paginationCursor,
    hasNoMorePreviousGridProducts,
    isLoadingMore,
    isWaitingForPreviousGridCursor,
    setCursorFromData,
    setIsLoadingMore
  } = useShopCategoryGridPagination({
    categoryId,
    gridId,
    previousGridId
  });

  // skip fetching when there is no category, a chained upstream grid has not
  // published its cursor yet, or there are no more products to fetch
  const shouldSkipQuery = S.isEmpty(categoryId) || isWaitingForPreviousGridCursor || hasNoMorePreviousGridProducts;
  const {
    data,
    loading,
    error,
    fetchMore
  } = useGetUngatedPaginatedShopCategoryQuery({
    // each grid manages cursor chaining locally, so avoid cache reuse across inserted grids
    fetchPolicy: 'no-cache',
    variables: {
      businessLine,
      after: paginationCursor,
      first: limit,
      id: categoryId
    },
    skip: shouldSkipQuery,
    onCompleted: completedData => {
      // publish the current grid cursor for downstream grids before recording analytics success
      setCursorFromData(completedData);
      recordMark('get_ungated_paginated_shop_category_completed', {
        graph_query_succeeded: true,
        business_line: businessLine
      });
    },
    onError: () => {
      setCursorFromData();
      recordMark('get_ungated_paginated_shop_category_completed', {
        graph_query_succeeded: false,
        business_line: businessLine
      });
    }
  });
  const shopProductConnection = getShopProductConnection(data);
  const shopProducts = shopProductConnection?.nodes ?? [];
  const pageInfo = shopProductConnection?.pageInfo;
  const nextPageCursor = pageInfo?.endCursor;

  // a valid next cursor is required before Show More can request another page
  const canFetchNextPage = Boolean(pageInfo?.hasNextPage && nextPageCursor);
  const handleShowMoreClick = async () => {
    if (!canFetchNextPage || !nextPageCursor) {
      return;
    }

    // use local loading state because Apollo's initial loading flag does not cover this button state
    setIsLoadingMore(true);
    try {
      // fetch the next cursor page and merge it into the current grid
      const result = await fetchMore({
        variables: {
          businessLine,
          after: nextPageCursor,
          first: limit,
          id: categoryId || ''
        },
        updateQuery: (previousData, {
          fetchMoreResult
        }) => mergeShopProductPages(previousData, fetchMoreResult)
      });

      // publish the updated end cursor for downstream grids after the next page is merged
      setCursorFromData(result.data);
    } catch {
      // keep the current data and cursor so the user can retry the same page
    } finally {
      setIsLoadingMore(false);
    }
  };
  const showMoreButton = showMoreEnabled && canFetchNextPage ? <Box display="flex" style={{
    justifyContent: 'center'
  }} pt={1} pb={2}>
        <Button disabled={isLoadingMore || !canFetchNextPage} onClick={handleShowMoreClick} variant="outline-large">
          Show More
        </Button>
      </Box> : null;
  const renderContent = () => {
    if (loading || isWaitingForPreviousGridCursor) {
      // chained grids show placeholders while the previous grid determines their starting cursor
      return <LoadingShopCategory limit={limit} />;
    }
    if (error || hasNoMorePreviousGridProducts || shopProducts.length === 0) {
      // empty includes true empty data, failed requests, and exhausted previous-grid chains
      return <EmptyShopCategory categoryId={categoryId} />;
    }
    return <>
        <ShopProductCards categoryId={categoryId} pageSize={limit} shopProducts={shopProducts} promoCards={promoCards} />
        {showMoreButton}
      </>;
  };
  return <Box mt={verticalSpacing}>
      <ModuleWrapper alignment="center" moduleTitle={moduleTitle} moduleFooter={moduleFooter}>
        <Box maxWidth="1200px" mx="auto">
          {renderContent()}
        </Box>
      </ModuleWrapper>
    </Box>;
};